import { useMemo } from "react";
import useSWR from "swr";
import { swrJsonFetcher } from "@/hooks/swr/fetcher";
import { Chip, Empty, GridNote, Panel, SearchBox, SourceError, WalletLink } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { STATIC_DATA } from "../lib/data";
import { count, hoursSpan, isoDay, pct0, pctSigned, toneClass, usd } from "../lib/format";
import { matches } from "../lib/markets";
import { Link, useParam, useSearchParam } from "../lib/nav";
import { personaLabel, type SegmentsDoc, type Trader } from "../lib/wallet";

// Grouped by window, so a lifetime figure is never read against a 30-day one.
const COLS: Col<Trader>[] = [
	{
		key: "t",
		label: "Trader",
		csv: (r) => r.address,
		w: 118,
		grow: 2,
		sort: (r) => r.name,
		cell: (r) => <WalletLink address={r.address} label={r.name} />,
	},
	{ key: "p", label: "Persona", title: "Set by rules over 30 days of behaviour, not by an LLM", w: 84, prio: 2, sort: (r) => r.persona, cell: (r) => <Chip>{personaLabel(r.persona)}</Chip> },
	{ key: "pnl", label: "PnL life", title: "Lifetime realised, plus unredeemed positions on resolved markets", w: 76, right: true, sep: true, sort: (r) => r.pnl_lifetime, cell: (r) => <span className={toneClass(r.pnl_lifetime)}>{usd(r.pnl_lifetime)}</span> },
	{ key: "roi", label: "ROI life", title: "Lifetime profit as a share of money put in", w: 72, right: true, sort: (r) => r.roi, cell: (r) => <span className={toneClass(r.roi)}>{pctSigned(r.roi)}</span> },
	{ key: "w", label: "Win life", title: "Share of resolved positions that won, over the wallet's lifetime", w: 62, right: true, prio: 4, sort: (r) => r.win_rate, cell: (r) => pct0(r.win_rate) },
	{ key: "vl", label: "Vol life", title: "Dollars traded over the wallet's lifetime", w: 68, right: true, prio: 3, sort: (r) => r.volume_lifetime, cell: (r) => usd(r.volume_lifetime) },
	{ key: "v30", label: "Vol 30d", title: "Dollars traded in the 30-day window", w: 68, right: true, prio: 1, sep: true, sort: (r) => r.volume_30d, cell: (r) => usd(r.volume_30d) },
	{ key: "m", label: "Maker 30d", title: "Share of 30d fills as the resting order", w: 62, right: true, prio: 7, sort: (r) => r.maker_share, cell: (r) => (r.maker_share === null ? "–" : pct0(r.maker_share * 100)) },
	{ key: "h", label: "Hold 30d", title: "Median time from first buy to exit, over the 30-day window", w: 56, right: true, prio: 8, sort: (r) => r.hold_median_h, cell: (r) => hoursSpan(r.hold_median_h) },
	{ key: "o", label: "Open PnL", title: "Open positions marked to the latest price at the snapshot", w: 66, right: true, prio: 5, sep: true, sort: (r) => r.unrealized_pnl, cell: (r) => <span className={toneClass(r.unrealized_pnl)}>{usd(r.unrealized_pnl)}</span> },
	{ key: "x", label: "X", title: "X handle the trader published on their Polymarket profile", w: 100, grow: 1, prio: 6, cell: (r) => (r.x_username ? <a className="text-dim hover:text-up" href={`https://x.com/${r.x_username}`} target="_blank" rel="noreferrer" onClick={(e: MouseEvent) => e.stopPropagation()}>@{r.x_username}</a> : "–") },
	{ key: "f", label: "X followers", title: "Followers of the wallet's X account. Nothing to do with trading.", w: 76, right: true, prio: 9, sort: (r) => r.followers, cell: (r) => count(r.followers) },
];

export function TopTradersView() {
	const { data, error, isLoading, mutate } = useSWR<SegmentsDoc>("data/segments.json", swrJsonFetcher, STATIC_DATA);
	const [q, setQ] = useSearchParam();
	const [persona, setPersona] = useParam("persona");

	const rows = data?.top_traders ?? [];
	const personas = useMemo(() => {
		const wallets = new Map<string, number>();
		for (const r of rows) if (r.persona) wallets.set(r.persona, (wallets.get(r.persona) ?? 0) + 1);
		return [...wallets.entries()].sort((a, b) => b[1] - a[1]);
	}, [rows]);

	const shown = useMemo(
		() => rows.filter((r) => (!persona || r.persona === persona) && matches(q, r.name, r.x_username, r.address)),
		[rows, q, persona],
	);

	const body = error && !data ? (
		<SourceError source="the segments snapshot" onRetry={() => void mutate()} />
	) : data && shown.length === 0 ? (
		<Empty>No traders match.</Empty>
	) : undefined;

	return (
		<View>
			<ViewHeader
				title="top traders"
				meta={data ? `${count(shown.length)} wallets` : undefined}
				desc="Largest X-linked wallets from the segments snapshot. Handles are ones the traders published themselves."
			>
				<SearchBox value={q} onChange={setQ} placeholder="search name, @x, 0x" />
			</ViewHeader>
			{/* Height reserved so the grid does not drop when the chips arrive. */}
			<div className="no-scrollbar flex h-[21px] shrink-0 gap-1 overflow-x-auto">
				{personas.map(([id, wallets]) => {
						const on = persona === id;
						return (
							<button
								type="button"
								key={id}
								aria-pressed={on}
								onClick={() => setPersona(on ? null : id)}
								title="Filter to this persona. The number is how many wallets have it."
								className={`num flex shrink-0 items-center gap-1.5 border px-1.5 py-0.5 text-[10px] ${on ? "border-up bg-up/15 text-bright" : "border-line bg-panel text-fg hover:bg-raised"}`}
							>
								{personaLabel(id)}
								<span className="text-dim">{wallets}</span>
							</button>
						);
				})}
			</div>
			<Panel>
				<DataGrid
					label="Top traders"
					cols={COLS}
					rows={shown}
					rowKey={(r) => r.address}
					initialSort={{ key: "pnl", dir: "desc" }}
					csvName="top-traders"
					copyValue={(r) => r.address}
					loading={isLoading && !data}
					body={body}
				/>
				<GridNote>
					snapshot {isoDay(data?.generated_at)} · 30d window ending {isoDay(data?.window?.ending)} ·{" "}
					<Link href="/segments#top-traders" className="text-dim hover:text-up">full table in segments</Link>
				</GridNote>
			</Panel>
		</View>
	);
}
